import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldAlert,
  Hash,
  Clock4,
  Lock,
  FolderOpenDot,
  Loader2,
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Copy,
  Eye,
  AlertTriangle,
  FileSignature,
  Send,
  RefreshCw,
  Sparkles,
} from "lucide-react";

export default function SeguridadView() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [chapter, setChapter] = useState("Documento completo");
  const [blocks, setBlocks] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [sealing, setSealing] = useState(false);
  const [justSealed, setJustSealed] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || []);
        if (data.projects?.length) setSelected(data.projects[0]);
      } catch {
        toast.error("No se pudieron cargar tus proyectos");
      } finally {
        setLoadingProjects(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selected) return;
    loadBlocks(selected.project_id);
  }, [selected]);

  const loadBlocks = async (pid) => {
    setLoadingBlocks(true);
    try {
      const { data } = await api.get(`/projects/${pid}/blockchain`);
      setBlocks(data.blocks || []);
    } catch {
      setBlocks([]);
    } finally {
      setLoadingBlocks(false);
    }
  };

  const seal = async () => {
    if (!selected || sealing) return;
    setSealing(true);
    setJustSealed(null);
    try {
      const { data } = await api.post(`/projects/${selected.project_id}/blockchain/seal`, {
        chapter_name: chapter.trim() || "Documento completo",
      });
      setJustSealed(data);
      await loadBlocks(selected.project_id);
      toast.success("Tu trabajo quedó sellado en el historial inmutable");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo sellar el bloque");
    } finally {
      setSealing(false);
    }
  };

  const copyVerifyLink = (hash) => {
    const url = `${window.location.origin}/verify/${hash}`;
    navigator.clipboard.writeText(url);
    toast.success("Enlace copiado");
  };

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-10 max-w-5xl mx-auto" data-testid="blindaje-view">
      {/* Intro */}
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Seguridad
      </p>
      <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink leading-tight text-balance">
        Cómo blindamos tu tesis paso a paso.
      </h1>
      <p className="mt-3 text-[14px] text-ink-soft max-w-2xl">
        Aquí te explicamos —en lenguaje claro— cómo funciona el sistema de
        Blindaje Antiplagio Criptográfico, qué tienes que hacer en cada momento
        y qué pasa en cada escenario posible. Tu trabajo merece protección real.
      </p>

      {/* TL;DR */}
      <div className="mt-8 rounded-2xl border border-forest/20 bg-white p-6" data-testid="blindaje-tldr">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium inline-flex items-center gap-1.5">
          <Sparkles size={11} /> En una sola idea
        </p>
        <p className="mt-2 font-serif text-xl text-ink leading-snug text-balance">
          Cada vez que guardas o sellas tu tesis, generamos una huella SHA-256
          única y la encadenamos en un registro que <span className="italic text-forest">nadie puede modificar</span>.
          Eso te da prueba pública y matemática de que tú la escribiste primero.
        </p>
      </div>

      {/* Step-by-step */}
      <section className="mt-10" data-testid="blindaje-steps">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-4">
          Los 4 pasos del blindaje
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Step
            n="1"
            icon={FileSignature}
            title="Escribe en el editor"
            body="Trabaja normalmente. Cada vez que tu documento se guarda automáticamente, el sistema calcula una huella SHA-256 del texto y la anota en segundo plano (sin que tengas que hacer nada)."
            action="Solo escribe. El auto-sellado se activa cuando cambia el contenido."
          />
          <Step
            n="2"
            icon={ShieldCheck}
            title="Sella manualmente antes de compartir"
            body="Cuando vayas a mandarle el documento a tu asesor, jurado o coautor, presiona “Sellar en el Historial Inmutable”. Esto crea un bloque etiquetado por capítulo y lo encadena con tu sello anterior."
            action="Recomendado antes de cada entrega o asesoría."
          />
          <Step
            n="3"
            icon={Send}
            title="Comparte el enlace de verificación"
            body="Cada bloque genera un enlace público de tipo /verify/<hash>. Lo puedes pegar en el correo o en la portada del documento. Cualquier asesor puede abrirlo sin cuenta y ver tu fecha, hora y autoría."
            action="Copia el enlace desde la lista de bloques que aparece abajo."
          />
          <Step
            n="4"
            icon={Lock}
            title="Si alguien copia tu trabajo, demuestra la autoría"
            body="Si te roban la investigación, el enlace público es tu prueba. Muestra que la versión sellada existía bajo tu nombre antes que cualquier copia. La cadena hace imposible alterar la fecha sin destruir todos los sellos posteriores."
            action="Conserva el enlace público de tu sello más reciente."
          />
        </div>
      </section>

      {/* Seal action */}
      <section className="mt-10" data-testid="blindaje-seal-section">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
          Sellar ahora
        </p>
        <div className="rounded-2xl border border-rule bg-white p-5 sm:p-6">
          {loadingProjects ? (
            <p className="text-[13px] text-ink-soft">Cargando proyectos…</p>
          ) : projects.length === 0 ? (
            <div className="text-center py-6">
              <p className="font-serif text-lg text-ink">Aún no tienes proyectos.</p>
              <button
                onClick={() => navigate("/dashboard")}
                className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark"
              >
                Crear mi primer proyecto <ArrowRight size={13} />
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_240px] gap-3">
                <label className="block">
                  <span className="text-[10px] tracking-[0.18em] uppercase text-ink-soft font-medium">
                    Proyecto a sellar
                  </span>
                  <select
                    data-testid="blindaje-project-select"
                    value={selected?.project_id || ""}
                    onChange={(e) => {
                      const p = projects.find((x) => x.project_id === e.target.value);
                      setSelected(p || null);
                      setJustSealed(null);
                    }}
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
                  >
                    {projects.map((p) => (
                      <option key={p.project_id} value={p.project_id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] tracking-[0.18em] uppercase text-ink-soft font-medium">
                    Capítulo (opcional)
                  </span>
                  <input
                    data-testid="blindaje-chapter-input"
                    value={chapter}
                    onChange={(e) => setChapter(e.target.value)}
                    placeholder="Documento completo"
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-rule bg-white text-[13.5px] outline-none focus:border-forest focus:ring-2 focus:ring-forest/15"
                  />
                </label>
              </div>
              <button
                data-testid="blindaje-seal-btn"
                onClick={seal}
                disabled={!selected || sealing}
                className="mt-4 group inline-flex items-center gap-2 px-6 py-3 rounded-full text-[13.5px] font-medium text-[#1d2c22] bg-[#E0C58A] hover:bg-[#EBD8A8] disabled:opacity-60 transition-all shadow-[0_18px_36px_-12px_rgba(224,197,138,0.55)]"
              >
                {sealing ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                {sealing ? "Sellando…" : "Sellar en el Historial Inmutable"}
              </button>

              {justSealed && (
                <div
                  data-testid="blindaje-just-sealed"
                  className="mt-5 rounded-2xl bg-[#1d2c22] text-paper p-5 ring-1 ring-[#E0C58A]/30"
                >
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#E0C58A] inline-flex items-center gap-1.5">
                    <CheckCircle2 size={11} /> Sello confirmado
                  </p>
                  <p className="mt-2 font-serif text-lg text-paper leading-snug">
                    Tu trabajo ha sido sellado criptográficamente. Tienes pruebas matemáticas e históricas de que tú eres el autor original de esta investigación ante cualquier intento de robo o copia.
                  </p>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11.5px] font-mono">
                    <Field label="Hash SHA-256" value={justSealed.text_snapshot_hash} mono />
                    <Field label="Bloque previo" value={justSealed.previous_block_hash} mono />
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Link
                      to={`/verify/${justSealed.text_snapshot_hash}`}
                      target="_blank"
                      data-testid="blindaje-view-receipt"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-medium text-[#1d2c22] bg-[#E0C58A] hover:bg-[#EBD8A8]"
                    >
                      <Eye size={12} /> Ver mi recibo público
                    </Link>
                    <button
                      onClick={() => copyVerifyLink(justSealed.text_snapshot_hash)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] text-paper/90 bg-white/5 hover:bg-white/10 border border-white/10"
                    >
                      <Copy size={11} /> Copiar enlace
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Blocks list */}
      {selected && (
        <section className="mt-10" data-testid="blindaje-blocks">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
              Historial de sellos de “{selected.title}”
            </p>
            <button
              onClick={() => loadBlocks(selected.project_id)}
              className="inline-flex items-center gap-1 text-[11.5px] text-ink-soft hover:text-forest"
            >
              <RefreshCw size={11} /> Refrescar
            </button>
          </div>
          {loadingBlocks ? (
            <p className="text-[13px] text-ink-soft">Cargando cadena…</p>
          ) : blocks.length === 0 ? (
            <div className="rounded-2xl border border-rule bg-white p-6 text-center">
              <p className="text-[13.5px] text-ink-soft">
                Este proyecto aún no tiene sellos. Presiona el botón de arriba para crear el bloque génesis.
              </p>
            </div>
          ) : (
            <ol className="flex flex-col gap-3">
              {blocks.map((b, i) => (
                <li
                  key={b.block_id}
                  data-testid={`blindaje-block-${b.block_id}`}
                  className="rounded-2xl border border-rule bg-white p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium">
                        Bloque {blocks.length - i}{" "}
                        {b.previous_block_hash === "GENESIS" && "· génesis"}
                        {" · "}
                        <span className="text-ink-soft normal-case tracking-normal">
                          {new Date(b.timestamp).toLocaleString("es-MX", {
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </span>
                      </p>
                      <p className="mt-1 font-serif text-base text-ink">
                        {b.chapter_name}
                      </p>
                      <p className="mt-0.5 text-[11px] text-ink-soft">
                        {b.snapshot_length.toLocaleString("es-MX")} caracteres · disparado por{" "}
                        <span className="text-ink">
                          {b.triggered_by === "manual" ? "tu acción" : "auto-guardado"}
                        </span>
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1 rounded-full bg-forest/10 text-forest px-2 py-0.5 text-[10px] font-medium">
                      <CheckCircle2 size={10} /> {b.status}
                    </span>
                  </div>
                  <div className="mt-3 font-mono text-[11px] text-ink-soft break-all">
                    <span className="text-ink">SHA-256:</span> {b.text_snapshot_hash}
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-ink-soft/80 break-all">
                    <span className="text-ink-soft">prev:</span>{" "}
                    {b.previous_block_hash === "GENESIS" ? (
                      <span className="text-copper">GENESIS (primer bloque)</span>
                    ) : (
                      b.previous_block_hash
                    )}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Link
                      to={`/verify/${b.text_snapshot_hash}`}
                      target="_blank"
                      data-testid={`blindaje-receipt-${b.block_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-medium text-paper bg-forest hover:bg-forest-dark"
                    >
                      <ExternalLink size={11} /> Ver recibo público
                    </Link>
                    <button
                      onClick={() => copyVerifyLink(b.text_snapshot_hash)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] text-ink-soft hover:bg-paper-2"
                    >
                      <Copy size={11} /> Copiar enlace
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>
      )}

      {/* Scenarios */}
      <section className="mt-12" data-testid="blindaje-scenarios">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-4">
          Escenarios reales y qué hacer
        </p>
        <div className="flex flex-col gap-3">
          <Scenario
            tone="warn"
            icon={Send}
            title="Vas a mandarle tu tesis a un asesor o profesor"
            body="Sella el documento desde aquí. Luego, en el correo, anexa el enlace público de verificación junto al PDF/DOCX. Si tu asesor abre el enlace, ve fecha, hora, autor y huella SHA-256 sin necesidad de crear cuenta."
            cta="Sellar antes de enviar"
          />
          <Scenario
            tone="danger"
            icon={AlertTriangle}
            title="Sospechas que alguien copió fragmentos de tu investigación"
            body="Manda el enlace público de tu sello más antiguo a la coordinación académica. Es prueba matemática de que tu versión existía antes de la del otro. Si el otro también selló, comparen timestamps: la versión sellada primero gana."
            cta="Conserva el enlace público de tu primer sello"
          />
          <Scenario
            tone="ok"
            icon={Eye}
            title="Te piden “certificado” de que la tesis es tuya"
            body="No hay que hacer trámites. El enlace /verify/<hash> es el certificado. Es público, no requiere cuenta y se puede compartir por WhatsApp, correo o impreso (es una URL corta)."
            cta="Comparte el enlace tal cual"
          />
          <Scenario
            tone="warn"
            icon={RefreshCw}
            title="Hiciste cambios grandes después del último sello"
            body="No pasa nada. El sistema crea un sello nuevo cada vez que el texto cambia y han pasado al menos 60 segundos. Si quieres uno inmediato (porque vas a entregar), presiona el botón manual. Los sellos viejos no se borran: son la historia de tu tesis."
            cta="Vuelve a sellar manualmente"
          />
          <Scenario
            tone="ok"
            icon={Lock}
            title="¿Y si pierdo el correo, la cuenta o todo?"
            body="El sello vive en el ledger. El enlace /verify/<hash> sigue funcionando aunque borres tu cuenta. Por eso te recomendamos guardar (impresa o en otro correo) la URL de tu sello más reciente como respaldo físico."
            cta="Guarda el enlace en un lugar seguro"
          />
        </div>
      </section>

      {/* FAQ tech */}
      <section className="mt-12 rounded-2xl border border-rule bg-paper-2/40 p-6" data-testid="blindaje-faq">
        <p className="text-[10px] tracking-[0.22em] uppercase text-copper font-medium mb-3">
          Preguntas técnicas frecuentes
        </p>
        <Faq
          q="¿Qué es SHA-256 y por qué es seguro?"
          a="Es una función matemática que convierte cualquier texto en una huella única de 64 caracteres. Cambia una sola coma de tu tesis y la huella cambia entera. Es imposible (con la tecnología actual) crear dos textos distintos con la misma huella."
        />
        <Faq
          q="¿Por qué los bloques se encadenan?"
          a="Cada bloque guarda la huella del anterior. Si alguien quisiera reescribir el pasado (cambiar la fecha de un sello viejo), tendría que recalcular TODOS los sellos posteriores en cuestión de segundos. En la práctica, imposible."
        />
        <Faq
          q="¿Pueden ver mi contenido?"
          a="No. Solo guardamos la huella SHA-256, no el texto. Ni el equipo de unaTESIS, ni ningún visitante del enlace público, puede reconstruir tu tesis a partir de la huella."
        />
        <Faq
          q="¿Esto reemplaza el registro ante INDAUTOR / derechos de autor?"
          a="No lo reemplaza, lo complementa. INDAUTOR te da reconocimiento legal en México; nuestro sello te da prueba de existencia inmediata, pública y gratuita, útil para procesos académicos del día a día."
        />
      </section>
    </div>
  );
}

/* ---------- bits ---------- */

function Step({ n, icon: Icon, title, body, action }) {
  return (
    <div className="relative rounded-2xl border border-rule bg-white p-5">
      <span className="absolute -top-3 -left-3 grid place-items-center h-8 w-8 rounded-full bg-[#1d2c22] text-[#E0C58A] font-serif text-[14px] shadow-[0_8px_24px_-8px_rgba(20,32,24,0.4)]">
        {n}
      </span>
      <span className="inline-flex items-center gap-1.5 text-forest">
        <Icon size={14} />
      </span>
      <p className="mt-2 font-serif text-[17px] text-ink leading-snug">{title}</p>
      <p className="mt-1.5 text-[12.5px] text-ink-soft leading-relaxed">{body}</p>
      <p className="mt-3 text-[11px] tracking-[0.18em] uppercase text-copper font-medium">
        Acción: <span className="normal-case tracking-normal text-ink">{action}</span>
      </p>
    </div>
  );
}

function Scenario({ tone, icon: Icon, title, body, cta }) {
  const styles =
    tone === "danger"
      ? "border-[#B6533F]/30 bg-[#FBEFEA]"
      : tone === "warn"
      ? "border-copper/30 bg-[#FBF6EC]"
      : "border-forest/20 bg-white";
  const iconColor =
    tone === "danger" ? "text-[#B6533F]" : tone === "warn" ? "text-copper" : "text-forest";
  return (
    <div className={`rounded-2xl border ${styles} p-5`}>
      <div className="flex items-start gap-3">
        <span className={`grid place-items-center h-9 w-9 shrink-0 rounded-full bg-white border border-rule ${iconColor}`}>
          <Icon size={15} />
        </span>
        <div>
          <p className="font-serif text-[17px] text-ink leading-snug">{title}</p>
          <p className="mt-1 text-[12.5px] text-ink-soft leading-relaxed">{body}</p>
          <p className="mt-2 text-[11.5px] text-ink font-medium">→ {cta}</p>
        </div>
      </div>
    </div>
  );
}

function Faq({ q, a }) {
  return (
    <details className="group border-b border-rule last:border-b-0 py-3">
      <summary className="cursor-pointer list-none flex items-center justify-between gap-2 text-[13.5px] font-medium text-ink">
        {q}
        <span className="text-ink-soft group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <p className="mt-2 text-[12.5px] text-ink-soft leading-relaxed">{a}</p>
    </details>
  );
}

function Field({ label, value, mono }) {
  return (
    <div className="rounded-lg bg-white/5 ring-1 ring-white/10 px-3 py-2">
      <p className="text-[9.5px] tracking-[0.22em] uppercase text-paper/45">{label}</p>
      <p className={`mt-0.5 text-[11px] text-paper/90 break-all ${mono ? "font-mono" : ""}`}>
        {value}
      </p>
    </div>
  );
}
